import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Trash2, Bot, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const SUGGESTIONS = [
  "How do I add a new sale?",
  "Where do I see today's profit?",
  "How does low-stock warning work?",
  "How do I email the daily report?",
  "How is staff different from admin?",
];

export default function Help() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/help/history");
        setMessages(data || []);
      } catch (err) {
        // Silent — empty history is OK
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("Failed to load help history", err);
        }
      } finally {
        setBootstrapping(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: msg, created_at: new Date().toISOString() },
    ]);
    setLoading(true);
    try {
      const { data } = await api.post("/help/ask", { message: msg });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.answer, created_at: new Date().toISOString() },
      ]);
    } catch (err) {
      const reason = formatApiError(err);
      toast.error(reason);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `⚠️ ${reason}`, created_at: new Date().toISOString() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    try {
      await api.delete("/help/history");
      setMessages([]);
      toast.success("Conversation cleared");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="help-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-navy flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-gold" /> Help
          </h1>
          <p className="text-sm text-muted-foreground">
            Ask anything about the CRM — how to add a sale, where reports live, role permissions, etc.
          </p>
        </div>
        {messages.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={clear}
            data-testid="help-clear-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear chat
          </Button>
        )}
      </div>

      <Card className="section-card p-0 overflow-hidden flex flex-col" style={{ minHeight: "60vh" }}>
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-6 space-y-4"
          style={{ maxHeight: "60vh" }}
          data-testid="help-messages"
        >
          {bootstrapping ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : messages.length === 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-muted-foreground">
                <div className="w-10 h-10 rounded-md bg-gold/10 text-gold flex items-center justify-center">
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium text-navy">Hi! I'm your CRM Help Assistant.</div>
                  <div className="text-sm">Pick a question below or type your own.</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {SUGGESTIONS.map((q, i) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    data-testid={`help-suggestion-${i}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-border/70 text-navy hover:bg-gold/10 hover:border-gold transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, idx) => (
              <div
                key={`${m.created_at}-${idx}`}
                className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`help-msg-${m.role}-${idx}`}
              >
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-md bg-gold/10 text-gold flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4" />
                  </div>
                )}
                <div
                  className={`max-w-[78%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-navy text-white"
                      : "bg-muted text-navy"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex gap-3 justify-start" data-testid="help-thinking">
              <div className="w-8 h-8 rounded-md bg-gold/10 text-gold flex items-center justify-center">
                <Bot className="w-4 h-4" />
              </div>
              <div className="bg-muted rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking...
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border/60 p-4 bg-white">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-end gap-2"
          >
            <Textarea
              data-testid="help-input"
              rows={2}
              placeholder="Ask a question about the CRM..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              className="resize-none"
              disabled={loading}
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="help-send-btn"
              className="bg-navy hover:bg-navy-hover text-white h-[60px]"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <div className="text-[10px] text-muted-foreground mt-2 text-right">
            Powered by Claude Sonnet 4.5 · Press <kbd className="px-1 border rounded">Enter</kbd> to send, <kbd className="px-1 border rounded">Shift+Enter</kbd> for newline
          </div>
        </div>
      </Card>
    </div>
  );
}
